import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { authService, guestXPHelper } from '../services/api';
import { applyPublicLanguage, getPreferredPublicLanguage, googleLocaleForPublicLanguage } from '../utils/publicLanguage';
import { clearPostAuthRedirect, resolvePostAuthRedirect } from '../utils/postAuthRedirect';
import BrandLogo from '../components/BrandLogo';
import './Auth.css';

function getEffectiveSubscriptionTier(user = {}) {
  if (user.role === 'admin') return 'pro';
  return user.subscriptionTier || 'plus';
}

function isProOrUltraTier(tier) {
  return ['pro', 'ultra'].includes(String(tier || '').toLowerCase());
}

function getEffectiveAiEntitlements(user = {}) {
  if (user.role === 'admin') {
    return {
      ...(user.aiEntitlements || {}),
      subscriptionTier: 'pro',
      canUseAI: true,
      canSyncAIMemory: true,
      canUsePracticeContext: true,
      aiMemoryScope: 'cloud',
    };
  }
  if (user.aiEntitlements) return user.aiEntitlements;

  const subscriptionTier = getEffectiveSubscriptionTier(user);
  const hasCloudFeatures = isProOrUltraTier(subscriptionTier);
  return {
    subscriptionTier,
    canUseAI: true,
    canSyncAIMemory: hasCloudFeatures,
    canUsePracticeContext: hasCloudFeatures,
    aiMemoryScope: hasCloudFeatures ? 'cloud' : subscriptionTier === 'plus' ? 'device' : 'none',
  };
}

function LoginPage({ setIsAuthenticated, setIsGuest, setEmailVerified }) {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [suspended, setSuspended] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was redirected here after mid-session suspension
  const wasSuspendedMidSession = location.state?.suspended;
  const sessionExpired = location.state?.sessionExpired;
  // Optional post-auth destination (e.g. resuming cohort registration).
  const safeRedirect = resolvePostAuthRedirect(location.search);

  useEffect(() => {
    applyPublicLanguage(i18n);
  }, [i18n]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const storeAuthAndNavigate = async (data) => {
    const user = data.user;
    localStorage.setItem('token', data.token);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('userId', user.id);
    localStorage.setItem('username', user.username);
    localStorage.setItem('userFullName', user.fullName || '');
    localStorage.setItem('userEmail', user.email || '');
    localStorage.setItem('userRole', user.role || 'user');
    localStorage.setItem('subscriptionTier', getEffectiveSubscriptionTier(user));
    localStorage.setItem('aiEntitlements', JSON.stringify(getEffectiveAiEntitlements(user)));
    localStorage.setItem('emailVerified', String(!!user.emailVerified));
    if (user.preferredVoice) {
      localStorage.setItem('preferredVoice', user.preferredVoice);
    }
    if (user.preferredVoices && typeof user.preferredVoices === 'object') {
      localStorage.setItem('preferredVoices', JSON.stringify(user.preferredVoices));
      Object.entries(user.preferredVoices).forEach(([language, voice]) => {
        if (voice) localStorage.setItem(`preferredVoice:${language}`, voice);
      });
    }
    localStorage.setItem('nativeLanguage', user.nativeLanguage || '');
    localStorage.setItem('targetLanguage', user.targetLanguage || '');
    // Keep the setup flag in sync with the account so a stale 'true' from an
    // abandoned setup attempt can't bounce a fully set-up user to the picker.
    if (user.languageSetupComplete === false) {
      localStorage.setItem('needsLanguageSetup', 'true');
    } else {
      localStorage.removeItem('needsLanguageSetup');
    }
    localStorage.removeItem('guestMode');
    guestXPHelper.clear();
    setIsGuest(false);
    setIsAuthenticated(true);
    setEmailVerified(!!user.emailVerified);

    // Switch UI language to user's saved preference
    i18n.changeLanguage(user.nativeLanguage || '');

    // Honor an explicit post-auth destination first (e.g. finishing a cohort
    // registration the user started before signing in).
    if (safeRedirect) {
      navigate(safeRedirect);
      clearPostAuthRedirect(safeRedirect);
      return;
    }

    // Sign-in always lands on Home; its continue card reopens unfinished work.
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const guestXP = guestXPHelper.get();
      const response = await authService.login(formData.email, formData.password, guestXP);
      await storeAuthAndNavigate(response.data);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.reason) {
        setSuspended({
          message: err.response.data.message,
          reason: err.response.data.reason,
        });
        setError('');
      } else {
        setError(err.response?.data?.message || t('login.loginFailed', "Login failed"));
        setSuspended(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setSuspended(null);
    setLoading(true);
    try {
      const guestXP = guestXPHelper.get();
      // Only forward a pair the user explicitly confirmed in the picker (e.g.
      // during a guest session). The landing page pre-seeds raw values without
      // confirmation — those must not silently become a new account's pair.
      const pairConfirmed = localStorage.getItem('languagePairConfirmed') === 'true';
      const nativeLang = pairConfirmed ? (localStorage.getItem('nativeLanguage') || '') : '';
      const targetLang = pairConfirmed ? (localStorage.getItem('targetLanguage') || '') : '';
      const response = await authService.googleLogin(
        credentialResponse.credential,
        guestXP,
        nativeLang,
        targetLang
      );
      // Incomplete account (no explicit pair yet) — let them pick languages
      // first. Sign-in itself has already succeeded at this point.
      if (!response.data.user.languageSetupComplete) {
        const data = response.data;
        const user = data.user;
        localStorage.setItem('token', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('userId', user.id);
        localStorage.setItem('username', user.username);
        localStorage.setItem('userFullName', user.fullName || '');
        localStorage.setItem('userEmail', user.email || '');
        localStorage.setItem('userRole', user.role || 'user');
        localStorage.setItem('subscriptionTier', getEffectiveSubscriptionTier(user));
        localStorage.setItem('aiEntitlements', JSON.stringify(getEffectiveAiEntitlements(user)));
        localStorage.setItem('emailVerified', String(!!user.emailVerified));
        localStorage.removeItem('guestMode');
        guestXPHelper.clear();
        setIsGuest(false);
        setIsAuthenticated(true);
        setEmailVerified(!!user.emailVerified);
        localStorage.setItem('needsLanguageSetup', 'true');
        const params = new URLSearchParams({ mode: 'google-setup' });
        if (safeRedirect) params.set('redirect', safeRedirect);
        navigate(`/select-language?${params.toString()}`);
        return;
      }
      await storeAuthAndNavigate(response.data);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.reason) {
        setSuspended({
          message: err.response.data.message,
          reason: err.response.data.reason,
        });
        setError('');
      } else {
        setError(err.response?.data?.message || t('login.googleFailed', "Google sign-in failed. Please try again."));
        setSuspended(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuestMode = () => {
    navigate('/select-language?mode=guest');
  };

  const signupParams = new URLSearchParams({ mode: 'register' });
  if (safeRedirect) signupParams.set('redirect', safeRedirect);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <BrandLogo variant="lockup" className="auth-logo" decorative={false} />
        <h1>{t('login.welcomeBack', "Welcome Back!")}</h1>

        {wasSuspendedMidSession && !suspended && (
          <div className="suspended-notice">
            <div className="suspended-icon">&#x1F6AB;</div>
            <h3>{t('login.accountSuspended', "Account Suspended")}</h3>
            <p>{t('login.suspendedLoggedOut', "Your account has been suspended. You have been logged out.")}</p>
            <p className="suspended-contact">{t('login.contactSupport', "Please contact support for more information.")}</p>
          </div>
        )}

        {sessionExpired && !suspended && (
          <div className="error">
            {t('login.sessionExpired', 'Your session expired. Please sign in again.')}
          </div>
        )}

        {suspended && (
          <div className="suspended-notice">
            <div className="suspended-icon">&#x1F6AB;</div>
            <h3>{t('login.accountSuspended', "Account Suspended")}</h3>
            <p>{suspended.message}</p>
            <p className="suspended-reason"><strong>{t('login.reason', "Reason")}:</strong> {suspended.reason}</p>
            <p className="suspended-contact">{t('login.suspendedMistake', "If you believe this is a mistake, please contact support.")}</p>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        <div className="google-btn-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError(t('login.googleFailed', "Google sign-in failed. Please try again."))}
            theme="outline"
            size="large"
            width="340"
            text="continue_with"
            shape="pill"
            locale={googleLocaleForPublicLanguage(getPreferredPublicLanguage())}
            use_fedcm_for_button={false}
          />
        </div>

        <div className="auth-divider">
          <span>{t('common.or', "or")}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('login.email', "Email")}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('login.emailPlaceholder', "Enter your email")}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">{t('login.password', "Password")}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('login.passwordPlaceholder', "Enter your password")}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('login.hidePassword', "Hide password") : t('login.showPassword', "Show password")}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('login.loggingIn', "Logging in...") : t('login.loginButton', "Login")}
          </button>
        </form>

        <p style={{ textAlign: 'right', marginTop: '8px' }}>
          <Link to="/forgot-password" style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}>
            {t('login.forgotPassword', 'Forgot password?')}
          </Link>
        </p>

        <div className="auth-divider">
          <span>{t('common.or', "or")}</span>
        </div>

        <button className="btn btn-guest" onClick={handleGuestMode}>
          {t('login.tryAsGuest', "Try as Guest")}
        </button>
        <p className="guest-note">{t('login.guestNote', "No account needed - explore the app freely!")}</p>

        <p className="auth-link">
          {t('login.noAccount', "Don't have an account?")} <Link to={`/select-language?${signupParams.toString()}`}>{t('login.signUpHere', "Sign up here")}</Link>
        </p>
        <p className="auth-link">
          <Link to="/certificates/verify">{t('certificates.lookupLink', 'Verify a certificate')}</Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
